const { formatCurrency, formatDate, formatTime, getShortTransactionLogId } = require('./helpers')
const { getAgentTopupProfitCents } = require('./agent-rate-conversions')

const AGENT_TRANSACTION_LOG_TYPE_OPTIONS = [
  { text: 'Purchased credit', value: 'purchased_credit' },
  { text: 'Top up', value: 'passenger_credit' },
  { text: 'Refund', value: 'refund' },
  { text: 'Credit refund', value: 'passenger_credit_reversal' },
  { text: 'Deduction', value: 'passenger_credit_deduction' },
  { text: 'Gift sent', value: 'agent_gift_debit' },
  { text: 'Gift received', value: 'agent_gift_credit' },
  { text: 'Gift refund', value: 'agent_gift_refund' },
  { text: 'Gift declined', value: 'agent_gift_rejected' },
  { text: 'Gift cancelled', value: 'agent_gift_cancelled' }
]

const LOG_TYPE_ICON_DATA = {
  purchased_credit: {
    icon: 'fas fa-plus',
    color: '#000',
    label: 'purchased credit'
  },
  passenger_credit: {
    icon: 'fas fa-paper-plane',
    color: '#000',
    label: 'top up'
  },
  refund: {
    icon: 'fas fa-undo',
    color: '#000',
    label: 'refund'
  },
  passenger_credit_reversal: {
    icon: 'fas fa-undo',
    color: '#000',
    label: 'credit refund'
  },
  passenger_credit_deduction: {
    icon: 'fas fa-share',
    color: '#000',
    label: 'deduction'
  },
  agent_gift_debit: {
    icon: 'fas fa-gift',
    color: '#000',
    label: 'gift sent'
  },
  agent_gift_credit: {
    icon: 'fas fa-gift',
    color: '#000',
    label: 'gift received'
  },
  agent_gift_refund: {
    icon: 'fas fa-undo',
    color: '#000',
    label: 'gift refund'
  },
  agent_gift_rejected: {
    icon: 'fas fa-ban',
    color: '#000',
    label: 'gift declined'
  },
  agent_gift_cancelled: {
    icon: 'far fa-ban',
    color: '#000',
    label: 'gift cancelled'
  }
}

const DEFAULT_LOG_TYPE_ICON_DATA = {
  icon: 'fas fa-circle',
  color: '#000',
  label: ''
}

const LOG_STATUS_ICON_DATA = {
  confirmed: { icon: 'fas fa-check', color: '#4CAF50' },
  completed: { icon: 'fas fa-check', color: '#4CAF50' },
  processing: { icon: 'far fa-hourglass-half', color: '#000' },
  pending: { icon: 'far fa-hourglass-half', color: '#000' },
  cancelled: { icon: 'far fa-ban', color: '#000' },
  rejected: { icon: 'fas fa-exclamation-triangle', color: '#FFC107' },
  error: { icon: 'fas fa-exclamation-triangle', color: '#f65122' },
  timedout: { icon: 'fas fa-hourglass-end', color: '#f65122' }
}

const DEFAULT_LOG_STATUS_ICON_DATA = {
  icon: 'fas fa-circle',
  color: '#000'
}

const LOG_STATUS_LABELS = {
  confirmed: 'confirmed',
  completed: 'completed',
  processing: 'processing',
  pending: 'pending',
  cancelled: 'cancelled',
  rejected: 'rejected',
  error: 'error',
  timedout: 'timed out'
}

const ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS = {
  passenger_credit: 'passenger top up'
}

const GIFT_COUNTERPARTY_ROW_LABELS = {
  agent_gift_debit: 'Sent to',
  agent_gift_credit: 'From',
  agent_gift_refund: 'Gift refunded from',
  agent_gift_rejected: 'From',
  agent_gift_cancelled: 'From'
}

const GIFT_AWAITING_ACCEPTANCE_LABEL = 'awaiting acceptance'

const GIFT_STATUS_HISTORY_LABELS = {
  pending: 'awaiting acceptance',
  accepted: 'accepted',
  declined: 'declined',
  cancelled: 'cancelled'
}

function formatGiftCounterpartyLabel (name, phone) {
  const trimmedName = name ? String(name).trim() : ''
  const trimmedPhone = phone ? String(phone).trim() : ''
  if (trimmedName && trimmedPhone) {
    return trimmedName + ' (' + trimmedPhone + ')'
  }
  return trimmedName || trimmedPhone
}

function truncateGiftCounterpartyName (name, maxLength) {
  const trimmed = name ? String(name).trim() : ''
  const limit = maxLength || 10
  if (trimmed.length <= limit) {
    return trimmed
  }
  return trimmed.slice(0, limit) + '...'
}

function formatGiftCounterpartyListLabel (name, phone) {
  return formatGiftCounterpartyLabel(truncateGiftCounterpartyName(name, 10), phone)
}

function getGiftCounterpartyRowLabel (logType, logMetadata) {
  if (logType === 'agent_gift_rejected' && logMetadata && logMetadata.giftRole === 'sender') {
    return 'Sent to'
  }
  return GIFT_COUNTERPARTY_ROW_LABELS[logType] || ''
}

function titleCaseLabel (label) {
  if (!label) {
    return label
  }
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function getLogTypeIconData (logType, labelOverrides) {
  const iconData = LOG_TYPE_ICON_DATA[logType] || DEFAULT_LOG_TYPE_ICON_DATA
  const label = labelOverrides && labelOverrides[logType]
  if (!label) {
    return iconData
  }
  return Object.assign({}, iconData, { label: label })
}

function getAgentTransactionLogTypeOptions (labelOverrides) {
  return AGENT_TRANSACTION_LOG_TYPE_OPTIONS.map(function (option) {
    const label = labelOverrides && labelOverrides[option.value]
    if (!label) {
      return Object.assign({}, option)
    }
    return Object.assign({}, option, { text: titleCaseLabel(label) })
  })
}

function formatSelectedLogTypeSummary (selectedValues, options) {
  const selected = selectedValues || []
  if (!selected.length) {
    return ''
  }
  const labelsByValue = {}
  const optionList = options || []
  optionList.forEach(function (option) {
    if (option && option.value) {
      labelsByValue[option.value] = option.text
    }
  })
  return selected.map(function (value) {
    return labelsByValue[value] || value
  }).join(', ')
}

function filterTransactionsByLogTypes (rows, logTypes) {
  if (!logTypes || !logTypes.length) {
    return rows || []
  }
  return (rows || []).filter(function (row) {
    return row && logTypes.indexOf(row.logType) !== -1
  })
}

function getLogStatusIconData (logStatus) {
  return LOG_STATUS_ICON_DATA[logStatus] || DEFAULT_LOG_STATUS_ICON_DATA
}

function formatLogStatusLabel (logStatus) {
  if (!logStatus) {
    return ''
  }
  return LOG_STATUS_LABELS[logStatus] || String(logStatus).replace(/_/g, ' ')
}

function hasConversionRate (conversionRatePercent) {
  if (conversionRatePercent === null || conversionRatePercent === undefined || conversionRatePercent === '') {
    return false
  }
  return Number.isFinite(Number(conversionRatePercent))
}

function shouldShowPassengerCreditRate (logType, conversionRatePercent) {
  return logType === 'passenger_credit' && hasConversionRate(conversionRatePercent)
}

function formatConversionRatePercentLabel (logType, conversionRatePercent) {
  if (!shouldShowPassengerCreditRate(logType, conversionRatePercent)) {
    return ''
  }
  return Number(conversionRatePercent) + '%'
}

function getDisplayProfitCents (logType, amountAsCents, conversionRatePercent) {
  if (!shouldShowPassengerCreditRate(logType, conversionRatePercent)) {
    return null
  }
  return getAgentTopupProfitCents({
    topupAmountAsCents: amountAsCents,
    conversionRatePercent: conversionRatePercent
  })
}

function formatProfitLabel (logType, amountAsCents, conversionRatePercent, currencyPrefix) {
  const profitCents = getDisplayProfitCents(logType, amountAsCents, conversionRatePercent)
  if (profitCents === null) {
    return ''
  }
  return formatCurrency(profitCents, currencyPrefix || '')
}

function formatDateTimeLabel (date) {
  if (!date) {
    return ''
  }
  return formatDate(date) + ' - ' + formatTime(date)
}

function formatLogMetadataError (logMetadata) {
  if (!logMetadata || logMetadata.error == null || logMetadata.error === '') {
    return ''
  }
  if (typeof logMetadata.error === 'string') {
    return logMetadata.error
  }
  return JSON.stringify(logMetadata.error, null, 2)
}

function formatTransactionErrorLabel (transaction) {
  if (!transaction) {
    return ''
  }
  const logStatus = transaction.logStatus
  if (logStatus !== 'error' && logStatus !== 'timedout') {
    return ''
  }
  const errorText = formatLogMetadataError(transaction.logMetadata)
  if (errorText) {
    return errorText
  }
  if (logStatus === 'timedout') {
    return 'Transaction timed out'
  }
  return ''
}

function relationRecord (transaction, key) {
  if (!transaction) {
    return null
  }
  const value = transaction[key]
  if (typeof value === 'function') {
    return value.call(transaction)
  }
  return value || null
}

function getAgentGiftIdFromTransaction (transaction) {
  const metadata = (transaction && transaction.logMetadata) || {}
  return metadata.agentGiftId || ''
}

function formatGiftStatusHistoryLabel (status) {
  if (!status) {
    return ''
  }
  return GIFT_STATUS_HISTORY_LABELS[status] || String(status).replace(/_/g, ' ')
}

function buildGiftStatusHistory (gift) {
  const history = []
  if (!gift) {
    return history
  }
  if (gift.dateCreated) {
    history.push({ status: 'pending', date: gift.dateCreated })
  }
  if (gift.dateAccepted) {
    history.push({ status: 'accepted', date: gift.dateAccepted })
    return history
  }
  if (gift.dateCancelled) {
    const declined = !!(gift.giftMetadata && gift.giftMetadata.cancelledBy === 'recipient')
    history.push({
      status: declined ? 'declined' : 'cancelled',
      date: gift.dateCancelled
    })
  }
  return history
}

function mapStatusHistoryForDisplay (logMetadata) {
  const history = (logMetadata && logMetadata.statusHistory) || []
  return history.filter(function (entry) {
    return !!(entry && entry.status)
  }).map(function (entry) {
    return {
      status: entry.status,
      statusLabel: formatGiftStatusHistoryLabel(entry.status),
      date: entry.date || '',
      dateLabel: formatDateTimeLabel(entry.date)
    }
  })
}

function resolveGiftStatusHistory (mapped) {
  const fromMetadata = mapStatusHistoryForDisplay(mapped && mapped.logMetadata)
  if (fromMetadata.length) {
    return fromMetadata
  }
  if (mapped && mapped.statusHistory && mapped.statusHistory.length) {
    return mapped.statusHistory
  }
  return []
}

function findMatchingGift (transaction, gifts) {
  const giftId = getAgentGiftIdFromTransaction(transaction)
  if (!giftId || !gifts || !gifts.length) {
    return null
  }
  return gifts.find(function (gift) {
    return gift && gift.id === giftId
  }) || null
}

function overlayPendingOutgoingGift (mappedTransaction, pendingGifts) {
  const mapped = mappedTransaction || {}
  const matchingGift = findMatchingGift(mapped, pendingGifts)
  const isPendingOutgoingGift = !!(
    matchingGift &&
    mapped.logType === 'agent_gift_debit' &&
    incomingGiftOutcome(matchingGift) === 'pending'
  )
  const statusHistory = resolveGiftStatusHistory(mapped)
  if (!isPendingOutgoingGift) {
    return Object.assign({}, mapped, {
      isPendingOutgoingGift: false,
      isPendingIncomingGift: !!mapped.isPendingIncomingGift,
      pendingGiftId: mapped.pendingGiftId || '',
      statusHistory: statusHistory
    })
  }
  return Object.assign({}, mapped, {
    isPendingOutgoingGift: true,
    isPendingIncomingGift: false,
    pendingGiftId: matchingGift.id,
    logStatusIconData: getLogStatusIconData('pending'),
    logStatusLabel: GIFT_AWAITING_ACCEPTANCE_LABEL,
    statusHistory: statusHistory
  })
}

function incomingGiftOutcome (gift) {
  if (!gift || gift.dateAccepted) {
    return ''
  }
  if (gift.dateCancelled) {
    return gift.giftMetadata && gift.giftMetadata.cancelledBy === 'recipient' ? 'declined' : 'cancelled'
  }
  if (gift.status === 'rejected') {
    return 'declined'
  }
  if (gift.status === 'cancelled') {
    return 'cancelled'
  }
  if (gift.status && gift.status !== 'pending') {
    return ''
  }
  return 'pending'
}

function isIncomingGiftForAgent (gift, agentProfileId) {
  return !!(gift && agentProfileId && gift.toAgentProfileId === agentProfileId && incomingGiftOutcome(gift))
}

function incomingGiftDisplayType (outcome) {
  if (outcome === 'declined') {
    return 'agent_gift_rejected'
  }
  if (outcome === 'cancelled') {
    return 'agent_gift_cancelled'
  }
  return 'agent_gift_credit'
}

function incomingGiftDisplayStatus (outcome) {
  if (outcome === 'declined') {
    return 'rejected'
  }
  if (outcome === 'cancelled') {
    return 'cancelled'
  }
  return 'pending'
}

function incomingGiftStatusLabel (outcome) {
  if (outcome === 'declined') {
    return 'declined'
  }
  if (outcome === 'cancelled') {
    return 'cancelled'
  }
  return GIFT_AWAITING_ACCEPTANCE_LABEL
}

function mapIncomingGiftForDisplay (gift, options) {
  const opts = options || {}
  const currencyPrefix = opts.currencyPrefix || ''
  const outcome = incomingGiftOutcome(gift) || 'pending'
  const logType = incomingGiftDisplayType(outcome)
  const logStatus = incomingGiftDisplayStatus(outcome)
  const amount = gift && gift.amount ? gift.amount : 0
  const fromName = (gift && gift.fromAgentName) || ''
  const metadata = (gift && gift.giftMetadata) || {}
  const fromPhone = (gift && gift.fromAgentPhone) || metadata.sendFromPhone || ''
  const giftCounterpartyLabel = formatGiftCounterpartyLabel(fromName, fromPhone)
  const dateCreated = gift && gift.dateCreated
  const dateUpdated = (gift && gift.dateCancelled) || dateCreated
  const statusHistoryEntries = Array.isArray(metadata.statusHistory) ? metadata.statusHistory : []
  const logMetadata = {
    agentGiftId: gift && gift.id ? gift.id : '',
    giftRole: 'recipient',
    statusHistory: statusHistoryEntries
  }

  return {
    id: 'incoming-gift-' + (gift && gift.id ? gift.id : ''),
    pendingGiftId: gift && gift.id ? gift.id : '',
    isIncomingGiftOverlay: true,
    isPendingIncomingGift: outcome === 'pending',
    isDeclinedIncomingGift: outcome === 'declined',
    isCancelledIncomingGift: outcome === 'cancelled',
    isPendingOutgoingGift: false,
    logType: logType,
    logStatus: logStatus,
    logMetadata: logMetadata,
    statusHistory: mapStatusHistoryForDisplay(logMetadata),
    amount: amount,
    amountLabel: formatCurrency(amount, currencyPrefix),
    currencyPrefix: currencyPrefix,
    dateCreated: dateCreated,
    dateUpdated: dateUpdated,
    dateCreatedLabel: formatDateTimeLabel(dateCreated),
    dateUpdatedLabel: formatDateTimeLabel(dateUpdated),
    giftCounterpartyNameLabel: fromName,
    giftCounterpartyPhoneNumberLabel: fromPhone,
    giftCounterpartyLabel: giftCounterpartyLabel,
    giftCounterpartyRowLabel: getGiftCounterpartyRowLabel(logType),
    logTypeIconData: getLogTypeIconData(logType, opts.logTypeLabels),
    logStatusIconData: getLogStatusIconData(logStatus),
    logStatusLabel: incomingGiftStatusLabel(outcome),
    passengerPhoneNumberLabel: '',
    phoneNumberLabel: '',
    profileBalanceBeforeLabel: '',
    profileBalanceAfterLabel: '',
    idLabel: ''
  }
}

function mapIncomingGiftsForDisplay (gifts, options) {
  const opts = options || {}
  return (gifts || []).filter(function (gift) {
    return isIncomingGiftForAgent(gift, opts.agentProfileId)
  }).map(function (gift) {
    return mapIncomingGiftForDisplay(gift, opts)
  })
}

function mapPendingIncomingGiftsForDisplay (pendingGifts, options) {
  return mapIncomingGiftsForDisplay(pendingGifts, options)
}

function mapAgentTransactionForDisplay (transaction, options) {
  const opts = options || {}
  const currencyPrefix = opts.currencyPrefix || ''
  const logMetadata = (transaction && transaction.logMetadata) || {}
  const momo = relationRecord(transaction, 'agentMobileMoneyAccount')
  const passengerPhone = logMetadata.passengerPhoneNumber || ''
  const momoPhone = (momo && momo.phoneNumber) || ''
  const amount = transaction && transaction.amount
  const conversionRatePercent = transaction && transaction.conversionRatePercent
  const giftCounterpartyName = logMetadata.giftCounterpartyName || ''
  const giftCounterpartyPhoneNumber = logMetadata.giftCounterpartyPhoneNumber || ''
  const giftCounterpartyLabel = formatGiftCounterpartyLabel(giftCounterpartyName, giftCounterpartyPhoneNumber)

  return Object.assign({}, transaction, {
    idLabel: transaction && transaction.id ? getShortTransactionLogId(transaction.id) : '',
    passengerPhoneNumberLabel: passengerPhone,
    phoneNumberLabel: momoPhone,
    giftCounterpartyNameLabel: giftCounterpartyName,
    giftCounterpartyPhoneNumberLabel: giftCounterpartyPhoneNumber,
    giftCounterpartyLabel: giftCounterpartyLabel,
    giftCounterpartyRowLabel: getGiftCounterpartyRowLabel(transaction && transaction.logType, logMetadata),
    dateCreatedLabel: formatDateTimeLabel(transaction && transaction.dateCreated),
    dateUpdatedLabel: formatDateTimeLabel(transaction && transaction.dateUpdated),
    amountLabel: formatCurrency(amount, currencyPrefix),
    currencyPrefix: currencyPrefix,
    profileBalanceBeforeLabel: transaction && (transaction.profileBalanceBefore || transaction.profileBalanceBefore === 0)
      ? formatCurrency(transaction.profileBalanceBefore)
      : '',
    profileBalanceAfterLabel: transaction && (transaction.profileBalanceAfter || transaction.profileBalanceAfter === 0)
      ? formatCurrency(transaction.profileBalanceAfter)
      : '',
    conversionRatePercentLabel: formatConversionRatePercentLabel(transaction && transaction.logType, conversionRatePercent),
    profitCents: getDisplayProfitCents(transaction && transaction.logType, amount, conversionRatePercent),
    profitLabel: formatProfitLabel(transaction && transaction.logType, amount, conversionRatePercent, currencyPrefix),
    logTypeIconData: getLogTypeIconData(transaction && transaction.logType, opts.logTypeLabels),
    logStatusIconData: getLogStatusIconData(transaction && transaction.logStatus),
    logStatusLabel: formatLogStatusLabel(transaction && transaction.logStatus),
    errorLabel: formatTransactionErrorLabel(transaction),
    statusHistory: mapStatusHistoryForDisplay(logMetadata)
  })
}

module.exports = {
  AGENT_TRANSACTION_LOG_TYPE_OPTIONS: AGENT_TRANSACTION_LOG_TYPE_OPTIONS.slice(),
  ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS: Object.assign({}, ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS),
  GIFT_COUNTERPARTY_ROW_LABELS: Object.assign({}, GIFT_COUNTERPARTY_ROW_LABELS),
  GIFT_AWAITING_ACCEPTANCE_LABEL: GIFT_AWAITING_ACCEPTANCE_LABEL,
  getLogTypeIconData,
  getAgentTransactionLogTypeOptions,
  formatSelectedLogTypeSummary,
  filterTransactionsByLogTypes,
  getLogStatusIconData,
  formatLogStatusLabel,
  formatConversionRatePercentLabel,
  formatProfitLabel,
  formatLogMetadataError,
  formatGiftCounterpartyLabel,
  formatGiftCounterpartyListLabel,
  getGiftCounterpartyRowLabel,
  formatTransactionErrorLabel,
  formatGiftStatusHistoryLabel,
  buildGiftStatusHistory,
  mapStatusHistoryForDisplay,
  overlayPendingOutgoingGift,
  mapIncomingGiftsForDisplay,
  mapPendingIncomingGiftsForDisplay,
  mapAgentTransactionForDisplay
}
